'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';

import { Policy } from '@flipster/contentful-graphql/codegen/types.generated';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { useCheckSpotOnly } from '@flipster/features/src/shared/regulation';
import { useSupportedLocale } from '@flipster/i18n/hooks';
import { useRouter } from '@flipster/i18n/routing';
import { createTabsComponent } from '@flipster/react/design-system/tabs';
import { consentBunja } from '@flipster/utils/consent/state';

const Tabs = createTabsComponent<string>();

const policiesPathPrefix = '/policies/';

interface PoliciesTabsProps {
  page: string;
  policies?: Partial<Policy>[];
}

export const PoliciesTabs = ({ page, policies: policiesProp }: PoliciesTabsProps) => {
  const isSpotOnly = useCheckSpotOnly();
  const router = useRouter();

  const locale = useSupportedLocale();
  const { isEeaCountryAtom } = useBunja(consentBunja);
  const { user, isLoading } = useAuth();
  const isEeaCountry = useAtomValue(isEeaCountryAtom);
  const isPolandOffboard = user?.isPolandOffboard;

  const policies = useMemo(() => {
    if (!policiesProp) return policiesProp;

    return policiesProp.filter((policy) => policy.isVaraRegulated === null || policy.isVaraRegulated === isSpotOnly);
  }, [policiesProp, isSpotOnly]);

  useEffect(() => {
    if (!isLoading && page === 'poland-offboarding' && !isPolandOffboard) {
      router.replace(`/${locale}/not-found`);
    }
  }, [page, isPolandOffboard, isLoading, router, locale]);

  // Filter out cookies tab if not in EEA country
  const visiblePolicies = policies?.filter((policy) => {
    if (policy.slug === 'cookies' && !isEeaCountry) {
      return false;
    }
    if (policy.slug === 'poland-offboarding' && !isPolandOffboard) {
      return false;
    }
    return true;
  });
  return (
    <Tabs type='scrollable' value={page} onClick={(tab) => router.replace(policiesPathPrefix + tab)}>
      {visiblePolicies?.map(({ tabName, slug }) => (
        <Tabs.Tab key={slug} value={slug ?? ''} label={tabName} />
      ))}
    </Tabs>
  );
};
